import Pusher, {Channel} from "pusher-js";

/**
 * Single Pusher connection per tab, shared by all subscribers.
 *
 * Channels are reference-counted and unsubscribed when the last handler is
 * released; the connection itself is never torn down. Without
 * REACT_APP_PUSHER_APP_KEY the client stays null and subscribing is a no-op.
 */

export type PusherHandler = (payload: any) => void

interface Entry {
    channel: Channel
    binds: Set<PusherHandler>
}

let client: Pusher | null = null
let created = false
const entries = new Map<string, Entry>()

export const getPusher = (): Pusher | null => {
    if (created) return client
    created = true

    const key = process.env.REACT_APP_PUSHER_APP_KEY
    if (!key) return null

    client = new Pusher(key, {
        cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER!!
    })
    return client
}

/**
 * Binds `handler` to `event`, subscribing to the channel on first use.
 * Returns an idempotent unbind that must be called on unmount.
 */
export const subscribe = (
    name: string,
    event: string,
    handler: PusherHandler
): () => void => {
    const pusher = getPusher()
    if (!pusher) return () => {
    }

    let entry = entries.get(name)
    if (!entry) {
        entry = {channel: pusher.subscribe(name), binds: new Set()}
        entries.set(name, entry)
    }

    entry.binds.add(handler)
    entry.channel.bind(event, handler)

    let released = false
    return () => {
        if (released) return
        released = true

        entry!.channel.unbind(event, handler)
        entry!.binds.delete(handler)
        if (entry!.binds.size > 0) return

        entries.delete(name)
        pusher.unsubscribe(name)
    }
}
